import React from 'react';

import styles from './processFlow.module.scss';

import { faClass } from '@/constants/icons';

/**
 * Stage-by-stage audit trail of a process, most recent last.
 *
 * Answers "where has this practice been stuck?" — the entry still open shows a
 * running duration, which is usually the reason someone opened the trail.
 *
 * Presentational: durations and labels are formatted by the caller so this
 * component stays free of date and i18n concerns.
 *
 * @param {Array<{stage, entered_at, exited_at, by, notes}>} props.entries
 * @param {(stageKey: string) => {label: string, icon?: string, variant?: string}} props.resolveStage
 * @param {(entry: object) => string} props.formatWhen
 * @param {(entry: object) => string} props.formatDuration
 * @param {string} [props.emptyLabel]
 */
export default function ProcessHistory({
  entries = [],
  resolveStage,
  formatWhen,
  formatDuration,
  emptyLabel = '—',
}) {
  if (!entries.length) {
    return <p className={styles.historyEmpty}>{emptyLabel}</p>;
  }

  return (
    <ol className={styles.history}>
      {entries.map((entry, index) => {
        const stage = resolveStage?.(entry.stage) || { label: entry.stage };
        const open = !entry.exited_at;
        return (
          <li
            key={`${entry.stage}-${entry.entered_at}-${index}`}
            className={styles.historyItem}
            data-open={open ? 'true' : 'false'}
            data-variant={stage.variant || 'info'}
          >
            <span className={styles.historyMarker} aria-hidden="true">
              {stage.icon && <i className={faClass(stage.icon)} />}
            </span>
            <div className={styles.historyBody}>
              <span className={styles.historyStage}>{stage.label}</span>
              <span className={styles.historyMeta}>
                {formatWhen?.(entry)}
                {entry.by ? ` · ${entry.by}` : ''}
              </span>
              {entry.notes && <span className={styles.historyNotes}>{entry.notes}</span>}
            </div>
            <span className={styles.historyDuration}>{formatDuration?.(entry)}</span>
          </li>
        );
      })}
    </ol>
  );
}
